import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { getQuoteCalculations } from "../../api/quote/getQuoteCalculations";
import { getQuoteFinanceComparisonCalculations } from "../../api/quote/getQuoteFinanceComparisonCalculations";
import { getQuoteInclusionCalculations } from "../../api/quote/getQuoteInclusionCalculations";
import APIconButton from "../../components/buttons/APIconButton";
import BackButton from "../../components/buttons/BackButton";
import PageContainer from "../../components/PageContainer";
import { downloadFile } from "../../helpers/download";
import { useApi } from "../../providers/ApiProvider";
import CreateQuoteUrlSection from "./components/CreateQuoteUrlSection";
import FinanceComparisonCalculations, {
  FINANCE_COMPARISON_ALL_FIELDS,
} from "./components/finance-comparison/FinanceComparisonCalculations";
import FinanceComparisonCarLoanCalculations, {
  FINANCE_COMPARISON_CAR_LOAN_ALL_FIELDS,
} from "./components/finance-comparison/FinanceComparisonCarLoanCalculations";
import FinanceComparisonCashCalculations, {
  FINANCE_COMPARISON_CASH_ALL_FIELDS,
} from "./components/finance-comparison/FinanceComparisonCashCalculations";
import FinanceComparisonHomeLoanCalculations, {
  FINANCE_COMPARISON_HOME_LOAN_ALL_FIELDS,
} from "./components/finance-comparison/FinanceComparisonHomeLoanCalculations";
import InclusionCalculations from "./components/inclusions/InclusionCalculations";
import LuxuryLeaseCalculations, {
  LUXURY_LEASE_ALL_FIELDS,
  LUXURY_LEASE_EXTERNAL_FIELDS,
} from "./components/luxury-lease/LuxuryLeaseCalculations";
import SalaryComparisonCalculations, {
  SALARY_COMPARISON_ALL_FIELDS,
} from "./components/luxury-lease/SalaryComparisonCalculations";

type CalculationPage =
  | "luxury-lease-external"
  | "luxury-lease-internal"
  | "salary-comparison-internal"
  | "finance-comparison-internal"
  | "finance-comparison-cash-internal"
  | "finance-comparison-home-loan-internal"
  | "finance-comparison-car-loan-internal"
  | "inclusions-internal";

const pagesWithQuoteDownload: CalculationPage[] = [
  "luxury-lease-internal",
  "salary-comparison-internal",
  "finance-comparison-internal",
  "finance-comparison-cash-internal",
  "finance-comparison-home-loan-internal",
  "finance-comparison-car-loan-internal",
];

const ViewQuoteCalculations = () => {
  const { quoteId } = useParams();
  const { fetchWithAuth } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCalculation = searchParams.get(
    "calculation"
  ) as CalculationPage;

  if (!quoteId) {
    // TODO: improve client side error messaging
    throw new Error("quote id param is missing");
  }

  const calculations = getQuoteCalculations(quoteId);
  const financeComparisons = getQuoteFinanceComparisonCalculations(quoteId);
  const inclusions = getQuoteInclusionCalculations(quoteId);

  if (calculations.isError) {
    throw calculations.error;
  }

  if (financeComparisons.isError) {
    throw financeComparisons.error;
  }

  if (inclusions.isError) {
    throw inclusions.error;
  }

  const isLoading =
    calculations.isFetching ||
    financeComparisons.isFetching ||
    inclusions.isFetching;
  const isSuccess =
    calculations.isSuccess &&
    financeComparisons.isSuccess &&
    inclusions.isSuccess;

  let pageContent: ReactNode;
  let isContentPage = false;

  const download = async (source: "ap" | "catch-e") => {
    const pdf = await fetchWithAuth<Blob>("getQuotePDF", quoteId, {
      queryParams: {
        useCatchE: source === "catch-e" ? "true" : undefined,
        useAP: source === "ap" ? "true" : undefined,
      },
      responseOptions: { responseType: "blob" },
    });

    downloadFile(pdf, `${quoteId}-${source}-quote.pdf`);
  };

  const onCalculationLinkClick = (calculation: CalculationPage) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("calculation", calculation); // Add or update the 'foo' query parameter
    setSearchParams(newSearchParams);
  };

  const renderCalculationLink = (
    name: string,
    calculation: CalculationPage,
    selected: boolean = false
  ) => {
    return (
      <Typography
        variant="bodyLarge"
        color={selected ? undefined : "primary"}
        onClick={() => !selected && onCalculationLinkClick(calculation)}
        sx={
          selected
            ? undefined
            : { textDecoration: "underline", cursor: "pointer" }
        }
      >
        {name}
      </Typography>
    );
  };

  const renderCalculationSelector = (
    pages: { name: string; page: CalculationPage; selected?: boolean }[]
  ) => {
    return (
      <Box sx={{ display: "flex", gap: 2 }}>
        {pages.map((page) => (
          <Box key={page.page}>
            {renderCalculationLink(page.name, page.page, page.selected)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderFinanceComparisonSelector = (selected: CalculationPage) => {
    return renderCalculationSelector([
      {
        name: "Comparison",
        page: "finance-comparison-internal",
        selected: selected === "finance-comparison-internal",
      },
      {
        name: "Cash",
        page: "finance-comparison-cash-internal",
        selected: selected === "finance-comparison-cash-internal",
      },
      {
        name: "Home Loan",
        page: "finance-comparison-home-loan-internal",
        selected: selected === "finance-comparison-home-loan-internal",
      },
      {
        name: "Car Loan",
        page: "finance-comparison-car-loan-internal",
        selected: selected === "finance-comparison-car-loan-internal",
      },
    ]);
  };

  if (isSuccess) {
    switch (selectedCalculation) {
      case "luxury-lease-external":
        pageContent = (
          <Box>
            <LuxuryLeaseCalculations
              calculations={calculations.data}
              displayedFields={LUXURY_LEASE_EXTERNAL_FIELDS}
            />
          </Box>
        );
        break;
      case "luxury-lease-internal":
        pageContent = (
          <Box>
            <LuxuryLeaseCalculations
              calculations={calculations.data}
              displayedFields={LUXURY_LEASE_ALL_FIELDS}
            />
          </Box>
        );
        break;
      case "salary-comparison-internal":
        pageContent = (
          <Box>
            <SalaryComparisonCalculations
              calculations={calculations.data}
              displayedFields={SALARY_COMPARISON_ALL_FIELDS}
            />
          </Box>
        );
        break;
      case "finance-comparison-internal":
        pageContent = (
          <Box>
            <FinanceComparisonCalculations
              calculations={financeComparisons.data}
              displayedFields={FINANCE_COMPARISON_ALL_FIELDS}
              pageLinks={renderFinanceComparisonSelector(
                "finance-comparison-internal"
              )}
            />
          </Box>
        );
        break;
      case "finance-comparison-cash-internal":
        pageContent = (
          <Box>
            <FinanceComparisonCashCalculations
              calculations={financeComparisons.data}
              displayedFields={FINANCE_COMPARISON_CASH_ALL_FIELDS}
              pageLinks={renderFinanceComparisonSelector(
                "finance-comparison-cash-internal"
              )}
            />
          </Box>
        );
        break;
      case "finance-comparison-home-loan-internal":
        pageContent = (
          <Box>
            <FinanceComparisonHomeLoanCalculations
              calculations={financeComparisons.data}
              displayedFields={FINANCE_COMPARISON_HOME_LOAN_ALL_FIELDS}
              pageLinks={renderFinanceComparisonSelector(
                "finance-comparison-home-loan-internal"
              )}
            />
          </Box>
        );
        break;
      case "finance-comparison-car-loan-internal":
        pageContent = (
          <Box>
            <FinanceComparisonCarLoanCalculations
              calculations={financeComparisons.data}
              displayedFields={FINANCE_COMPARISON_CAR_LOAN_ALL_FIELDS}
              pageLinks={renderFinanceComparisonSelector(
                "finance-comparison-car-loan-internal"
              )}
            />
          </Box>
        );
        break;
      case "inclusions-internal":
        pageContent = (
          <Box>
            <InclusionCalculations calculations={inclusions.data} />
          </Box>
        );
        break;
      default:
        isContentPage = true;
        pageContent = (
          <Box>
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Typography variant="headlineSmall" sx={{ mb: 1 }}>
                  For External Use
                </Typography>
                {renderCalculationLink(
                  "Luxury Lease Calculations (External)",
                  "luxury-lease-external"
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="headlineSmall" sx={{ mb: 1 }}>
                  Internal Use ONLY
                </Typography>

                {renderCalculationLink(
                  "Luxury Lease Calculations (Internal)",
                  "luxury-lease-internal"
                )}
                {renderCalculationLink(
                  "Salary Comparison Calculations (Internal)",
                  "salary-comparison-internal"
                )}
                {renderCalculationLink(
                  "Finance Comparison Calculations (Internal)",
                  "finance-comparison-internal"
                )}
                {renderCalculationLink(
                  "Inclusions Calculations (Internal)",
                  "inclusions-internal"
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="headlineSmall" sx={{ mb: 1 }}>
                  Create Quote URL
                </Typography>
                <CreateQuoteUrlSection quoteId={quoteId} />
              </Stack>
            </Stack>
          </Box>
        );
    }
  }

  const showDownloadButton =
    selectedCalculation &&
    pagesWithQuoteDownload.indexOf(selectedCalculation) !== -1;

  return (
    <PageContainer
      loading={isLoading}
      size={isContentPage ? undefined : "full"}
      title={isContentPage ? "Quote Calculations" : ""}
    >
      {showDownloadButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            flexDirection: "column",
            alignItems: "end",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
            <Typography variant="bodyMedium">AP Quote:</Typography>
            <APIconButton iconName="download" onClick={() => download("ap")} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
            <Typography variant="bodyMedium">Catch-e Quote:</Typography>
            <APIconButton
              iconName="download"
              onClick={() => download("catch-e")}
            />
          </Box>
        </Box>
      )}
      {isSuccess && (
        <>
          {pageContent}
          <Box sx={{ mt: 8, displayPrint: "none" }}>
            <BackButton />
          </Box>
        </>
      )}
    </PageContainer>
  );
};

export default ViewQuoteCalculations;
